// DocumentStructure assembles batches + statement info from raw scanner output,
// producing the public API defined at PRD § 2.6. Stable statement IDs live in
// `ids.ts`; line/column math in `lineIndex.ts`; kind classification in
// `coarseKind.ts`.

import type {
  Batch,
  BatchSeparator,
  DocumentStructure,
  ParseOptions,
  ParsedDocument,
  RecoveryInfo,
  SqlEngine,
  SqlRange,
  StatementInfo,
  StatementKind,
} from '../model';
import { SCHEMA_VERSION } from '../model';
import { buildEditorContext } from './editorContextProjection';

import { classifyStatement } from './classifyStatement';
import { extractSchemaEffects } from './schemaEffects';
import { makeBatchId, statementId } from './ids';
import { buildLineIndex, rangeFromOffsets, type LineIndex } from './lineIndex';
import { scanDocument, type ScanBatchSeparator, type ScanResult, type ScanStatement } from './scanner';

function buildStatementInfo(s: ScanStatement, idx: LineIndex, engine: SqlEngine): StatementInfo {
  const { kind, risk } = classifyStatement(s.text, engine);
  return {
    id: statementId(s.text, s.startOffset),
    range: rangeFromOffsets(idx, s.startOffset, s.endOffset),
    kind,
    risk,
    readsFrom: [],
    writesTo: [],
    schemaEffects: extractSchemaEffects(s.text, engine),
  };
}

function separatorKind(sep: ScanBatchSeparator | undefined): BatchSeparator {
  if (!sep) return 'none';
  return sep.kind === 'GO' ? 'GO' : '/';
}

// `'unknown'`/unclassified statements are deliberately treated as
// NON-result-producing. `mapResult` maps the Nth result grid to the Nth
// result-producing statement in order; if a leading unclassifiable statement
// counted, it would absorb a later real SELECT's grid and shift every
// result→statement attribution after it by one. coarseKind is conservative —
// a genuinely result-producing statement gets classified ('select'/'call'/
// 'explain'); anything still 'unknown' is, by design, not assumed to yield a
// grid here (under-counting a hypothetical unknown-that-runs is far safer than
// mislabelling a real SELECT's data as another statement's).
function producesResult(kind: StatementKind): boolean {
  return kind === 'select' || kind === 'call' || kind === 'explain';
}

interface BuiltBatch {
  batch: Batch;
  scanSeparator?: ScanBatchSeparator;
  contentStart: number;
  contentEnd: number;
}

function assembleBatches(
  scan: ScanResult,
  source: string,
  idx: LineIndex,
  documentId: string,
  engine: SqlEngine,
): BuiltBatch[] {
  const statementsByBatch = new Map<number, StatementInfo[]>();
  for (const s of scan.statements) {
    const info = buildStatementInfo(s, idx, engine);
    const list = statementsByBatch.get(s.batchIndex);
    if (list) list.push(info); else statementsByBatch.set(s.batchIndex, [info]);
  }
  for (const flip of scan.delimiterFlips) {
    const info: StatementInfo = {
      id: statementId(source.slice(flip.startOffset, flip.endOffset), flip.startOffset),
      range: rangeFromOffsets(idx, flip.startOffset, flip.endOffset),
      kind: 'client-directive',
      risk: 'session',
      readsFrom: [],
      writesTo: [],
      schemaEffects: [],
    };
    const prior = scan.statements.filter((s) => s.endOffset <= flip.startOffset);
    const batchIdx = prior.length > 0 ? prior[prior.length - 1].batchIndex : 0;
    const list = statementsByBatch.get(batchIdx);
    if (list) list.push(info); else statementsByBatch.set(batchIdx, [info]);
  }
  const separatorByBatch = new Map<number, ScanBatchSeparator>();
  for (const sep of scan.batchSeparators) separatorByBatch.set(sep.batchIndex, sep);

  const built: BuiltBatch[] = [];
  for (let bi = 0; bi <= scan.maxBatchIndex; bi++) {
    const stmts = (statementsByBatch.get(bi) ?? [])
      .slice()
      .sort((a, b) => a.range.startOffset - b.range.startOffset);
    const sep = separatorByBatch.get(bi);
    if (stmts.length === 0 && !sep) continue;

    const startOffset = stmts.length > 0 ? stmts[0].range.startOffset : sep!.startOffset;
    const endOffset = sep ? sep.endOffset : stmts[stmts.length - 1].range.endOffset;
    const range = rangeFromOffsets(idx, startOffset, endOffset);
    const batch: Batch = {
      id: makeBatchId(documentId, bi, startOffset),
      range,
      separator: separatorKind(sep),
      statements: stmts,
    };
    built.push({
      batch,
      scanSeparator: sep,
      contentStart: startOffset,
      contentEnd: sep ? sep.startOffset : endOffset,
    });
  }
  return built;
}

export function getDocumentStructure(
  doc: ParsedDocument,
  documentId: string,
  // Optional pre-computed scan of `doc.source` with `doc.options`. When a caller
  // (e.g. buildResolverCompletionContext) has already scanned the exact same
  // source/options to build the ParsedDocument, it can pass that ScanResult here
  // to avoid a second identical full-source scan. Omitting it preserves the
  // legacy single-/two-arg behaviour: we re-scan. The scan is a pure function of
  // (source, options), so reuse is behaviour-preserving.
  precomputedScan?: ScanResult,
): DocumentStructure {
  const { source, options } = doc;
  const scan = precomputedScan ?? scanDocument(source, options);
  const idx = buildLineIndex(source);
  const built = assembleBatches(scan, source, idx, documentId, options.engine);
  const batches: Batch[] = built.map((b) => b.batch);
  const flat: StatementInfo[] = batches
    .flatMap((b) => b.statements)
    .slice()
    .sort((a, b) => a.range.startOffset - b.range.startOffset);

  return {
    documentId,
    batches,
    statements: flat,

    findStatements(range: SqlRange): StatementInfo[] {
      const { startOffset, endOffset } = range;
      const out: StatementInfo[] = [];
      // Full intersection scan, not an early-exit. The previous `break` on the
      // first `startOffset > endOffset` was only sound while `flat` held
      // strictly non-overlapping, start-sorted statements. Once procedural-index
      // / CTE-body work introduces nested statement ranges (a parent enclosing
      // child ranges), iteration order no longer tracks nesting and an early
      // `break` could terminate before an inner statement that genuinely
      // intersects the query span — silently omitting it. A full scan keeps
      // correctness independent of `flat` ordering/nesting; over the already
      // start-sorted non-nested case it still yields start-sorted results.
      for (const s of flat) {
        if (s.range.endOffset >= startOffset && s.range.startOffset <= endOffset) {
          out.push(s);
        }
      }
      return out;
    },

    statementAt(offset: number): StatementInfo | null {
      for (const s of flat) {
        if (offset >= s.range.startOffset && offset <= s.range.endOffset) return s;
        if (offset < s.range.startOffset) break;
      }
      return null;
    },

    splitExecutableUnits(): Array<{ batchId: string; sql: string; statements: StatementInfo[] }> {
      const units: Array<{ batchId: string; sql: string; statements: StatementInfo[] }> = [];
      for (const b of built) {
        const goCount = b.scanSeparator && b.scanSeparator.kind === 'GO' ? b.scanSeparator.goCount : 1;
        const sql = source.slice(b.contentStart, b.contentEnd).trim();
        for (let rep = 0; rep < goCount; rep++) {
          units.push({ batchId: b.batch.id, sql, statements: b.batch.statements });
        }
      }
      return units;
    },

    mapResult(resultIndex: number): { batchId: string; statementId: string } {
      let remaining = resultIndex;
      for (const batch of batches) {
        for (const s of batch.statements) {
          if (!producesResult(s.kind)) continue;
          if (remaining === 0) return { batchId: batch.id, statementId: s.id };
          remaining--;
        }
      }
      const last = flat[flat.length - 1];
      const lastBatch = batches[batches.length - 1];
      return { batchId: lastBatch?.id ?? '', statementId: last?.id ?? '' };
    },

    mapError(errorLine: number, errorColumn: number): { batchId: string; statementId: string | null } {
      for (const batch of batches) {
        if (errorLine < batch.range.startLine || errorLine > batch.range.endLine) continue;
        // Collect all statements whose line-range contains errorLine.
        const candidates = batch.statements.filter(
          (s) => errorLine >= s.range.startLine && errorLine <= s.range.endLine,
        );
        if (candidates.length === 0) return { batchId: batch.id, statementId: null };
        if (candidates.length === 1) return { batchId: batch.id, statementId: candidates[0].id };
        // Multiple statements share the same line (e.g. `SELECT 1; SELECT bad;`).
        // Use errorColumn to pick the best match: the last candidate whose
        // startColumn (the column at which it begins on errorLine) is ≤ errorColumn.
        // `candidates` are already sorted by startOffset (ascending) from `flat`.
        // For a statement that begins on a PRIOR line, startColumn refers to that
        // prior line, so we guard: only use startColumn as a tie-breaker when
        // the statement actually starts on errorLine.
        let best = candidates[0];
        for (const s of candidates) {
          if (s.range.startLine === errorLine && s.range.startColumn <= errorColumn) {
            best = s;
          }
        }
        return { batchId: batch.id, statementId: best.id };
      }
      return { batchId: '', statementId: null };
    },

    getEditorContext(offset: number) {
      return buildEditorContext(doc, offset);
    },
  };
}

// Build a minimal ParsedDocument from source + options. Future slices will
// replace this with a tree-sitter-backed parse; the shape stays stable.
export function buildParsedDocumentFromSource(
  source: string,
  options: ParseOptions,
  // Optional pre-computed scan of `source` with `options`. Mirrors the param on
  // `getDocumentStructure`: callers that scan once and feed the same ScanResult
  // to both helpers avoid a redundant full-source scan. Omitting it re-scans
  // (the legacy two-arg behaviour). Pure (source, options) → ScanResult, so
  // passing an externally-produced scan is behaviour-preserving.
  precomputedScan?: ScanResult,
): ParsedDocument {
  const scan = precomputedScan ?? scanDocument(source, options);
  const idx = buildLineIndex(source);
  const statements = scan.statements.map((s) => buildStatementInfo(s, idx, options.engine));
  const recovery: RecoveryInfo = {
    errorRanges: [],
    missingRanges: [],
    truncated: false,
    confidence: 'high',
  };
  return { source, options, statements, recovery, schemaVersion: SCHEMA_VERSION };
}
